<template>
	<Tooltip
		v-if="toolTipText"
		:trigger="['focus']"
		:placement="placement"
		:title="toolTipText"
		v-model:visible="visible"
		:overlayClassName="overlayClassName"
		:getPopupContainer="trigger => trigger.parentElement"
	/>
	<Input
		ref="innerField"
		v-bind="{ ...$attrs, ...$props, maxlength: inputMaxlength }"
		v-model:value="inputValue"
		:showCount="showCount"
		@focus="onFocus"
		@blur="onBlur"
	/>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick, computed, toRef } from 'vue';
import { Input, Tooltip } from 'ant-design-vue';

import { useLocaleData } from '@/locales/useLocaleData';

export default {
	components: {
		Input, Tooltip,
	},
	emits: ['update:modelValue'],
	props: {
		allowedFormatChecker: {
			type: Function,
			default(value) {
				return { valid: true, value };
			},
		},
		toolTipText: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '',
		},
		// String, Number or Null
		modelValue: {
			required: true,
		},
		maxlength: {
			type: Number,
			required: false,
		},
		// если False, тултип будет показан только при ошибке во время ввода
		// если True, тултип будет показан сразу при фокусе на поле
		showTooltipOnFocus: {
			type: Boolean,
			default: false,
		},
		// для InputDecimal
		// если False, невалидный ввод блокируется моментально
		// если True, если расчет на то, что валидное значение получится по мере ввода, а в промежутке будут невалидные. Тогда on blur происходит проверка, и поле очищается
		correctOnBlur: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		// для InputDecimal
		allowedSymbolsRegex: {
			type: String,
			default: '',
		},
	},
	setup(props, { emit }) {
		const visible = ref(false);
		const modelValue = toRef(props, 'modelValue');
		const validateValue = rawValue => {
			if (props.allowedSymbolsRegex.length > 0) {
				const regexp = new RegExp('^[' + props.allowedSymbolsRegex + ']*$');
				if (!regexp.test(rawValue)) {
					// show alert, don't update value
					visible.value = true;
					return;
				}
			}
			const { valid, value } = props.allowedFormatChecker(rawValue);
			// добавил эту проверку только ради тестов. Input.maxlength на самом деле само по себе работает
			// если длина нового значения уменьшилась, то разрешаем удаление символов
			if (props.maxlength != null && rawValue.length > props.maxlength && value.length >= modelValue.value.length) {
				return;
			}
			if (valid) {
				if (!props.showTooltipOnFocus) {
					visible.value = false;
				}
				emit('update:modelValue', value);
			} else {
				visible.value = true;
				// show alert
				if (props.correctOnBlur) {
					// update value only if correctOnBlur is true
					emit('update:modelValue', value);
				} else {
					const oldValidation = props.allowedFormatChecker(modelValue.value);
					// если старок значение было invalid, и новое значение короче, то обновляем значение
					// это позволяет пользователю удалять запрещенные символы из поля
					if (!oldValidation.valid && value.length < oldValidation.value.length) {
						emit('update:modelValue', value);
					}
				}
			}
		};
		const inputValue = computed({
			get: () => modelValue.value,
			set: validateValue,
		});
		const scrollHandler = () => visible.value = false;
		onMounted(() => window.addEventListener('scroll', scrollHandler, true));
		onBeforeUnmount(() => window.removeEventListener('scroll', scrollHandler));

		const onFocus = () => {
			if (props.showTooltipOnFocus) {
				visible.value = true;
			}
		}

		const onBlur = () => {
			visible.value = false;
			if (props.correctOnBlur) {
				const { valid } = props.allowedFormatChecker(modelValue.value);
				if (!valid) {
					emit('update:modelValue', '');
				}
			}
		}

		const { localeData, directionLTR } = useLocaleData();
		const placement = computed(() => directionLTR.value ? 'topLeft' : 'topRight');
		const overlayClassName = computed(() => 'input-restricted-tooltip-' + localeData.value.direction);
		const showCount = computed(() => props.maxlength !== undefined);
		const inputMaxlength = computed(() => {
			if (props.maxlength === undefined || String(modelValue.value).length > props.maxlength) {
				return undefined;
			}
			return props.maxlength;
		});

		const innerField = ref();
		const focus = () => {
			nextTick(() => innerField.value.focus());
		}
		return {
			visible,
			inputValue,
			placement,
			overlayClassName,
			showCount,
			inputMaxlength,
			onFocus,
			onBlur,
			innerField,
			focus,
			validateValue,
		}
	},
}
</script>

<style>
.input-restricted-tooltip-ltr .ant-tooltip-inner {
	text-align: left;
}
.input-restricted-tooltip-rtl .ant-tooltip-inner {
	text-align: right;
}
</style>
